/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from "react";
import * as moment from "moment";
import { FormattedDate } from "react-intl";
import { getFileDownloadBaseURL, isProduction } from "config";
import {
  Button,
  DataTable,
  InlineNotification,
  NotificationActionButton,
  RadioButton,
  RadioButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Toggle,
} from "carbon-components-react";

import { DisclosureStage, LoanTypes } from "@eave/shared/types/api";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import Notification from "underwriter_dashboard/hurdle_editors/Disclosures/Notification";

import APIClient from "../../../APIClient";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

const FontAwesome = require("react-fontawesome");
const styles = require("./styles.module.scss");

const prelimCDMessage = (signingDateInfo: CDWaitPeriodWarningInfo): React.ReactNode => {
  const formatDate = (civilDate: string) => moment(civilDate).format("MM/DD/YYYY");

  if (!signingDateInfo || !signingDateInfo.current) {
    return null;
  }
  return (
    <p>
      For current signing date of {formatDate(signingDateInfo.current)}, prelim CD must be signed by{" "}
      {formatDate(signingDateInfo.prelimCDSigningRequiredBy)}.
    </p>
  );
};

const cdWaitPeriodWarning = (signingDateInfo: CDWaitPeriodWarningInfo): React.ReactNode => {
  const formatDate = (civilDate: string) => moment(civilDate).format("MM/DD/YYYY");

  if (!signingDateInfo) {
    return <p>No closing date info</p>;
  }
  if (!signingDateInfo.current) {
    return <p>Closing date not set</p>;
  }
  if (!signingDateInfo.earliestAllowed) {
    return (
      <>
        <p>
          Earliest allowed signing date unknown. Reason: {signingDateInfo.earliestAllowedReason}
        </p>
        <p>
          For current signing date of {formatDate(signingDateInfo.current)}, prelim CD must be
          signed by {formatDate(signingDateInfo.prelimCDSigningRequiredBy)}.
        </p>
      </>
    );
  }
  if (moment(signingDateInfo.current).isBefore(moment(signingDateInfo.earliestAllowed))) {
    return (
      <>
        <p>
          Current signing date, {formatDate(signingDateInfo.current)}, is before earliest allowed of{" "}
          {formatDate(signingDateInfo.earliestAllowed)}.
        </p>
        <p>Reason: {signingDateInfo.earliestAllowedReason}</p>
        <p>
          For current signing date, prelim CD must be signed by{" "}
          {formatDate(signingDateInfo.prelimCDSigningRequiredBy)}.
        </p>
      </>
    );
  }
  return null;
};

const disclosureStageTextConverter = (disclosureStage: DisclosureStage) => {
  switch (disclosureStage) {
    case DisclosureStage.PreAppLoanEstimate:
      return "Pre-App Loan Estimate";
    case DisclosureStage.LoanEstimate:
      return "Initial Disclosure Package - Loan Estimate";
    case DisclosureStage.Redisclosure:
      return "Redisclosure - Loan Estimate";
    case DisclosureStage.PreliminaryClosingDisclosure:
      return "Preliminary/Redisclosure Closing Disclosure";
    case DisclosureStage.ClosingDisclosure:
      return "Closing Package - Closing Disclosure";
    case DisclosureStage.UnderwritingPackage:
      return "Underwriting Package";
    case DisclosureStage.InvestorReview:
      return "Investor Review";
    default:
      throw new Error(`disclosure stage name of "${disclosureStage}" not handled`);
  }
};

interface CDWaitPeriodWarningInfo {
  current: string;
  earliestAllowed: string;
  earliestAllowedReason: string;
  prelimCDSigningRequiredBy: string;
  missingBuyersAgentCommission: boolean;
}

interface SigningDateInfo extends CDWaitPeriodWarningInfo {
  cdWaitPeriodWarning: React.ReactNode;
  prelimCDInfo: React.ReactNode;
}

const Disclosures: React.FC<HurdleProps> = ({
  app,
  hurdle,
  onHide,
  createNewDisclosure,
}: HurdleProps) => {
  const [signingDateInfo, setSigningDateInfo] = useState<SigningDateInfo>(null);
  const [packageType, setPackageType] = useState<DisclosureStage>(
    DisclosureStage.UnderwritingPackage,
  );
  const [eSign, setESign] = useState<boolean>(false);
  const [bypassTRIDWarning, setBypassTRIDWarning] = useState<boolean>(false);
  const [bypassRateWarning, setBypassRateWarning] = useState<boolean>(false);

  const loadCDWaitPeriodInfo = useCallback(() => {
    const appId = app.id;
    APIClient.get(`/uw/loan_applications/${appId}/cd_wait_period`).then((resp: any) => {
      const info = {
        current: resp.signing_date,
        earliestAllowed: resp.earliest_allowed_signing_date,
        earliestAllowedReason: resp.earliest_allowed_signing_date_reason,
        prelimCDSigningRequiredBy: resp.prelim_cd_signing_required_by,
        missingBuyersAgentCommission: !resp.has_buyers_agent_commission,
      };

      setSigningDateInfo({
        ...info,
        cdWaitPeriodWarning: cdWaitPeriodWarning(info),
        prelimCDInfo: prelimCDMessage(info),
      });
    });
  }, [app.id]);

  useEffect(() => {
    loadCDWaitPeriodInfo();
  }, [loadCDWaitPeriodInfo]);

  const generateDisclosure = (stage: DisclosureStage, isESign: boolean): void => {
    let confirmations = [];
    if (
      stage === DisclosureStage.ClosingDisclosure &&
      signingDateInfo &&
      signingDateInfo.cdWaitPeriodWarning
    ) {
      confirmations = [
        ...confirmations,
        "Signing date doesn't follow CD wait period. Are you sure you'd like to continue?",
      ];
    }
    if (isESign) {
      confirmations = [...confirmations, "Are you sure you'd like to start esign?"];
    }

    for (let i = 0; i < confirmations.length; i++) {
      // eslint-disable-next-line no-restricted-globals,no-alert
      if (!confirm(confirmations[i])) {
        return;
      }
    }

    createNewDisclosure(app.id, stage, eSign).then(() => {
      if (stage === DisclosureStage.PreliminaryClosingDisclosure && eSign === true) {
        loadCDWaitPeriodInfo();
      }
    });
  };

  const renderDisclosureStage = () => {
    const noExternalID = !app.external_id;
    const rateNotLocked = !(app.active_rate && app.active_rate.locked);
    const disclosures = ((app && app.disclosures) || []).map(d => ({
      ...d,
      id: `${d.created_at}`,
    }));
    const missingBuyersAgentCommission =
      signingDateInfo && signingDateInfo.missingBuyersAgentCommission;
    const missingDUDetails =
      (app.computed_properties.loan_type_name === LoanTypes.CONFORMING ||
        app.computed_properties.loan_type_name === LoanTypes.HIGH_BALANCE) &&
      (!app.computed_properties.du_recommendation || !app.computed_properties.fnma_identifier);

    // Borrower-facing disclosures are disabled until external id is set, so that the identifier
    // DocMagic receives for the file (ie "worksheet number") stays the same throughout all disclosures.
    // This allows their internal checks (ie on the loan detail report) to be run correctly, for items
    // that change across disclosures.
    const disabled = noExternalID;

    // Closing disclosure generation has stricter restrictions to prevent us from getting into trouble.

    // Hard Constraints: system definitely won't let us generate, because these things are important. There should be a corresponding InlineNotification for each to explain to the user
    const closingDisabledSystemHardConstraints =
      noExternalID || missingDUDetails || rateNotLocked || missingBuyersAgentCommission;
    // Soft Constraints: soft because this relies on a 3rd party, and so might be flaky; can be overridden.
    const closingDisableSystemSoftConstraints =
      signingDateInfo && signingDateInfo.cdWaitPeriodWarning;
    const closingDisclosuresDisabled =
      closingDisabledSystemHardConstraints ||
      (closingDisableSystemSoftConstraints && !bypassTRIDWarning);

    const disablePreClosingDisclosure =
      (packageType === DisclosureStage.PreAppLoanEstimate ||
        packageType === DisclosureStage.LoanEstimate ||
        packageType === DisclosureStage.Redisclosure) &&
      !app.decision.calculated_rate.determined;

    interface RadioProps {
      disabled: boolean;
      labelText: string;
      value: DisclosureStage;
      id: DisclosureStage;
    }

    const getRadioProps = (disclosureType: DisclosureStage, d: boolean): RadioProps => {
      return {
        disabled: d,
        labelText: disclosureStageTextConverter(disclosureType),
        value: disclosureType,
        id: disclosureType,
      };
    };

    return (
      <>
        <Notification appId={app.id} />
        <div className={styles.flexDisplay}>
          <div className={styles.generateDisclosuresSection}>
            <h4>Generate disclosures</h4>
            <RadioButtonGroup
              legendText="Which package type would you like to generate?"
              name="radio-button-group"
              orientation="vertical"
              defaultSelected={DisclosureStage.UnderwritingPackage}
              onChange={value => {
                setPackageType(value);
              }}
            >
              <RadioButton {...getRadioProps(DisclosureStage.UnderwritingPackage, false)} />
              <RadioButton {...getRadioProps(DisclosureStage.PreAppLoanEstimate, false)} />
              <RadioButton {...getRadioProps(DisclosureStage.LoanEstimate, disabled)} />
              <RadioButton {...getRadioProps(DisclosureStage.Redisclosure, disabled)} />
              <RadioButton
                {...getRadioProps(DisclosureStage.PreliminaryClosingDisclosure, false)}
              />
              <RadioButton {...getRadioProps(DisclosureStage.ClosingDisclosure, false)} />
            </RadioButtonGroup>
            <br />
            <Toggle
              id="esign_toggle"
              labelText="E-Sign?"
              labelA="No"
              labelB="Yes"
              onChange={e => {
                setESign(e.target && e.target.checked);
              }}
            />
            <br />
            {closingDisabledSystemHardConstraints &&
              (packageType === DisclosureStage.PreliminaryClosingDisclosure ||
                packageType === DisclosureStage.ClosingDisclosure) && (
                <InlineNotification
                  hideCloseButton
                  lowContrast
                  kind="error"
                  notificationType="inline"
                  role="alert"
                  title="Prelim CD & CD Generation blocked"
                  subtitle={
                    <span>
                      The following are missing:
                      <ul>
                        {rateNotLocked && <li>- A locked rate</li>}
                        {missingDUDetails && <li>- DU Recommendation and DU Identifier</li>}
                        {noExternalID && <li>- External Loan Number</li>}
                        {missingBuyersAgentCommission && (
                          <li>- A Buyer&apos;s Agent Commission fee (amount can be $0)</li>
                        )}
                      </ul>
                    </span>
                  }
                />
              )}
            {packageType === DisclosureStage.UnderwritingPackage && eSign && (
              <InlineNotification
                hideCloseButton
                lowContrast
                kind="error"
                notificationType="inline"
                role="alert"
                title="Cannot generate an eSign Underwriting Package"
              />
            )}
            {packageType === DisclosureStage.ClosingDisclosure &&
              signingDateInfo &&
              signingDateInfo.cdWaitPeriodWarning && (
                <>
                  <InlineNotification
                    hideCloseButton
                    lowContrast
                    kind="error"
                    notificationType="inline"
                    role="alert"
                    title="Warning: TRID CD wait period violation"
                    subtitle={<span>{signingDateInfo.cdWaitPeriodWarning}</span>}
                    actions={[
                      !bypassTRIDWarning && (
                        <NotificationActionButton
                          key="dismiss_TRID_warning"
                          onClick={() => setBypassTRIDWarning(true)}
                        >
                          {bypassTRIDWarning ? "TRID Warning bypassed" : "Ignore"}
                        </NotificationActionButton>
                      ),
                    ]}
                  />
                </>
              )}
            {signingDateInfo && signingDateInfo.prelimCDInfo && (
              <InlineNotification
                hideCloseButton
                lowContrast
                kind="info"
                notificationType="inline"
                role="alert"
                title=""
                subtitle={<span>{signingDateInfo.prelimCDInfo}</span>}
              />
            )}
            {disablePreClosingDisclosure && (
              <InlineNotification
                hideCloseButton
                lowContrast
                kind="error"
                notificationType="inline"
                role="alert"
                title="Warning: Rate not finalized"
                subtitle={
                  <span>
                    Please check credit summary&apos;s &quot;Risk Layering Criteria&quot; to see
                    what is yet to be determined.
                  </span>
                }
                actions={[
                  !bypassRateWarning && (
                    <NotificationActionButton
                      key="dismiss_rate_warning"
                      onClick={() => setBypassRateWarning(true)}
                    >
                      {bypassRateWarning ? "Rate Warning bypassed" : "Ignore"}
                    </NotificationActionButton>
                  ),
                ]}
              />
            )}
            <Button
              kind="primary"
              onClick={() => generateDisclosure(packageType, eSign)}
              className={styles.generateDisclosuresButton}
              disabled={
                (closingDisabledSystemHardConstraints &&
                  packageType === DisclosureStage.PreliminaryClosingDisclosure) ||
                (closingDisclosuresDisabled && packageType === DisclosureStage.ClosingDisclosure) ||
                (packageType === DisclosureStage.UnderwritingPackage && eSign) ||
                (disablePreClosingDisclosure && !bypassRateWarning)
              }
            >
              {`Generate ${eSign ? "E-sign " : ""}${disclosureStageTextConverter(
                packageType as DisclosureStage,
              )}`}
            </Button>
          </div>
          <div className={styles.disclosuresList}>
            <DataTable
              rows={disclosures}
              headers={makeHeaderData({
                stage: "Type",
                created_at: "Created at",
                pdf_s3_key: "PDF",
                esign: "eSign?",
              })}
              isSortable
            >
              {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getToolbarProps,
                onInputChange,
                getTableContainerProps,
              }) => (
                <TableContainer title="Disclosure Packages" {...getTableContainerProps()}>
                  <TableToolbar {...getToolbarProps()} aria-label="data table toolbar">
                    <TableToolbarContent>
                      <TableToolbarSearch onChange={onInputChange} />
                    </TableToolbarContent>
                  </TableToolbar>
                  <Table size="compact">
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader key={header.key} {...getHeaderProps({ header })}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow key={row.id} {...getRowProps({ row })}>
                          {row.cells.map(cell => {
                            if (cell.info.header === "created_at") {
                              return (
                                <TableCell key={cell.id} id={cell.id}>
                                  <FormattedDate
                                    value={cell.value}
                                    year="numeric"
                                    month="numeric"
                                    day="numeric"
                                    hour="numeric"
                                    minute="numeric"
                                  />
                                </TableCell>
                              );
                            }
                            if (cell.info.header === "stage") {
                              return (
                                <TableCell key={cell.id}>
                                  {disclosureStageTextConverter(cell.value)}
                                </TableCell>
                              );
                            }
                            if (cell.info.header === "pdf_s3_key") {
                              return (
                                <TableCell key={cell.id}>
                                  <a
                                    href={getFileDownloadBaseURL(cell.value)}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    View PDF
                                  </a>
                                </TableCell>
                              );
                            }
                            if (cell.info.header === "esign") {
                              return (
                                <TableCell key={cell.id}>
                                  {cell.value && <FontAwesome name="check" />}
                                </TableCell>
                              );
                            }
                            return <TableCell key={cell.id}>{cell.value}</TableCell>;
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          </div>
        </div>
      </>
    );
  };

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide} hideTitle>
      {renderDisclosureStage()}
    </HurdleView>
  );
};

export default Disclosures;
